import React, { useState, useEffect, useRef, useCallback, Component } from 'react';
import ReactDOM from 'react-dom';
import { TelnyxRTC } from '@telnyx/webrtc';
import { Button } from './ui/button';
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, Loader2, PhoneCall, AlertTriangle, Pause, Play, Minus, X } from 'lucide-react';
import DTMFKeypad from './DTMFKeypad';
import { toast } from 'sonner';
import axios from 'axios';
import { getVoiceAudioConstraints, getHDVoiceCodecs, getNoiseFilteredStream } from '../utils/audioQuality';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Error Boundary component to catch WebRTC errors
class WebRTCErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('WebRTC Error Boundary caught error:', error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error.message || 'WebRTC error');
    }
  }

  render() {
    if (this.state.hasError) {
      if (!this.props.isOpen) return null;
      return ReactDOM.createPortal(
        <>
          <div className="fixed inset-0 z-[9998] bg-black/40" />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-full max-w-sm">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-red-600 to-red-500 px-4 py-3">
                <div className="flex items-center gap-2 text-white">
                  <AlertTriangle className="h-5 w-5" />
                  <p className="font-semibold text-sm">Call Service Unavailable</p>
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-slate-600 mb-1">Browser calling is not available.</p>
                <p className="text-sm text-slate-500 mb-4">The call will be placed using your phone instead.</p>
                <Button className="w-full" onClick={() => {
                  this.setState({ hasError: false, error: null });
                  this.props.onClose();
                }}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </>,
        document.body
      );
    }

    return this.props.children;
  }
}

function TelnyxWebRTCCallInner({
  isOpen,
  onClose,
  phoneNumber,
  leadId,
  leadName,
  companyName,
  onCallConnected,
  onCallEnded,
  onDurationUpdate,
  onError  // Called when WebRTC fails - allows fallback
}) {
  const [client, setClient] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle, connecting, ringing, active, ended
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [audioSettings, setAudioSettings] = useState(null);
  const [activeNoiseFilter, setActiveNoiseFilter] = useState(null); // 'rnnoise' | null
  const [isMinimized, setIsMinimized] = useState(false);

  const remoteAudioRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const callDurationRef = useRef(0);         // Tracks duration without stale closure
  const hasCalledRef = useRef(false);         // Prevents duplicate calls
  const callReachedActiveRef = useRef(false); // True once SIP INVITE was accepted
  const handleCallStateChangeRef = useRef(null); // Always-current callback ref
  const onErrorRef = useRef(onError);         // Stable ref to onError prop
  const onCallEndedRef = useRef(onCallEnded); // Stable ref to onCallEnded prop
  const onCallConnectedRef = useRef(onCallConnected);
  const mediaRecorderRef = useRef(null);      // MediaRecorder for call recording
  const recordingChunksRef = useRef([]);      // Accumulated audio chunks
  const leadIdRef = useRef(leadId);           // Stable ref to leadId prop
  const localMicStreamRef = useRef(null);     // Local mic stream for duplex recording
  const audioCtxRef = useRef(null);           // AudioContext for mixing streams
  const dialToneCtxRef = useRef(null);        // AudioContext for dial/ringback tone
  const rnnoiseCleanupRef = useRef(null);     // Noise filter cleanup function
  const dialToneIntervalRef = useRef(null);   // Interval for ringback cadence
  const [isRecording, setIsRecording] = useState(false);

  // Keep prop refs current without triggering re-renders
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onCallEndedRef.current = onCallEnded; }, [onCallEnded]);
  useEffect(() => { onCallConnectedRef.current = onCallConnected; }, [onCallConnected]);
  useEffect(() => { leadIdRef.current = leadId; }, [leadId]);

  // Keep callDurationRef in sync
  useEffect(() => { callDurationRef.current = callDuration; }, [callDuration]);

  // Ringback tone — 440+480 Hz, ~1s ring, ~3s pause (natural cadence)
  const startDialTone = useCallback(() => {
    stopDialTone();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      dialToneCtxRef.current = ctx;
      const playBurst = () => {
        if (!dialToneCtxRef.current || dialToneCtxRef.current.state === 'closed') return;
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();
        osc1.frequency.value = 440;
        osc2.frequency.value = 480;
        gain.gain.value = 0.06;
        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(ctx.destination);
        const now = ctx.currentTime;
        // Fade in/out for smoother sound
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.06, now + 0.05);
        gain.gain.setValueAtTime(0.06, now + 0.9);
        gain.gain.linearRampToValueAtTime(0, now + 1.0);
        osc1.start(now);
        osc2.start(now);
        osc1.stop(now + 1.05);
        osc2.stop(now + 1.05);
      };
      playBurst();
      dialToneIntervalRef.current = setInterval(playBurst, 4000); // 1s tone + 3s silence
    } catch (e) {
      console.error('Could not play dial tone:', e);
    }
  }, []);

  const stopDialTone = useCallback(() => {
    if (dialToneIntervalRef.current) {
      clearInterval(dialToneIntervalRef.current);
      dialToneIntervalRef.current = null;
    }
    if (dialToneCtxRef.current) {
      try { dialToneCtxRef.current.close(); } catch (e) {}
      dialToneCtxRef.current = null;
    }
  }, []);

  // Fetch audio settings on mount
  useEffect(() => {
    const init = async () => {
      try {
        const audioRes = await axios.get(`${API}/user/preferences/audio_settings`).catch(() => null);
        if (audioRes?.data?.value) {
          setAudioSettings(audioRes.data.value);
        }
      } catch (err) {
        // No audio settings saved — ignore
      }
    };
    init();
  }, []);

  // Listen for live device changes from the dialer's audio bar
  useEffect(() => {
    const onChange = (e) => {
      if (e?.detail) setAudioSettings(e.detail);
    };
    window.addEventListener('audio-settings-changed', onChange);
    return () => window.removeEventListener('audio-settings-changed', onChange);
  }, []);

  // Re-apply speaker selection whenever it changes (e.g. user picks a different
  // output device mid-call from the dialer's Audio Devices bar)
  useEffect(() => {
    const el = remoteAudioRef.current;
    const spk = audioSettings?.speaker_id;
    if (!el || !spk || !el.setSinkId) return;
    el.setSinkId(spk).catch((e) => console.error('Could not switch speaker:', e));
  }, [audioSettings?.speaker_id]);

  // Start recording — mixes remote stream + local mic for full duplex recording
  const startRecording = useCallback(async (remoteStream) => {
    if (!remoteStream || !window.MediaRecorder) return;
    try {
      recordingChunksRef.current = [];

      let streamToRecord = remoteStream;

      // Try to capture local mic and mix with remote for full duplex
      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
        });
        localMicStreamRef.current = localStream;

        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const remoteSource = audioCtx.createMediaStreamSource(remoteStream);
        const localSource = audioCtx.createMediaStreamSource(localStream);
        const destination = audioCtx.createMediaStreamDestination();

        remoteSource.connect(destination);
        localSource.connect(destination);

        audioCtxRef.current = audioCtx;
        streamToRecord = destination.stream;
      } catch (e) {
        console.error('Could not get local mic for recording, recording remote only:', e);
      }

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';
      const recorder = new MediaRecorder(streamToRecord, mimeType ? { mimeType } : {});
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (e) {
      console.warn('Could not start call recording:', e);
    }
  }, []);

  // Stop recording, upload blob, return recording_id or null
  const stopAndUpload = useCallback(async (duration) => {
    const recorder = mediaRecorderRef.current;
    setIsRecording(false);

    // Cleanup local mic stream and audio context
    if (localMicStreamRef.current) {
      localMicStreamRef.current.getTracks().forEach(t => t.stop());
      localMicStreamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) {}
      audioCtxRef.current = null;
    }

    if (!recorder || recorder.state === 'inactive') return null;

    try {
      const blob = await new Promise((resolve) => {
        recorder.onstop = () => {
          const type = recorder.mimeType || 'audio/webm';
          resolve(new Blob(recordingChunksRef.current, { type }));
        };
        recorder.stop();
      });

      if (blob.size < 500) return null;

      const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
      const formData = new FormData();
      formData.append('file', blob, `call-recording.${ext}`);
      formData.append('phone_number', phoneNumber);
      formData.append('duration_secs', String(duration));
      if (leadIdRef.current) formData.append('lead_id', leadIdRef.current);

      const res = await axios.post(`${API}/telnyx/recordings/webrtc-upload`, formData, {
        timeout: 30000,
      });
      return res.data.recording_id;
    } catch (e) {
      console.warn('Recording upload failed:', e);
      return null;
    }
  }, [phoneNumber]);

  const startDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
    durationIntervalRef.current = setInterval(() => {
      if (callStartTimeRef.current) {
        const elapsed = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
        setCallDuration(elapsed);
        if (onDurationUpdate) onDurationUpdate(elapsed);
      }
    }, 1000);
  }, [onDurationUpdate]);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const handleCallStateChange = useCallback((call) => {
    const state = call.state;

    switch (state) {
      case 'new':
      case 'trying':
      case 'requesting':
        setCallState('connecting');
        startDialTone();
        break;
      case 'recovering':
      case 'ringing':
      case 'early':
        setCallState('ringing');
        // Dial tone already playing from connecting state
        break;
      case 'active':
        stopDialTone();
        setCallState('active');
        callReachedActiveRef.current = true;
        callStartTimeRef.current = Date.now();
        startDurationTimer();

        // Attach remote audio stream — try call.remoteStream first, then poll for it
        const tryAttachAudio = (attempt) => {
          const stream = call.remoteStream;
          const el = remoteAudioRef.current;
          if (stream && el) {
            el.srcObject = stream;
            el.volume = 1.0;
            el.muted = false;
            el.play().catch(e => console.error('[Dialer] Audio play error:', e));

            // Apply speaker selection with fallback to system default if the
            // saved deviceId is stale (e.g. unplugged headset, browser profile change).
            const speakerId = audioSettings?.speaker_id;
            if (el.setSinkId) {
              const target = speakerId || '';
              el.setSinkId(target)
                .catch((e) => {
                  console.warn('[Dialer] setSinkId failed, falling back to default:', e?.name || e);
                  el.setSinkId('').catch(() => {});
                });
            }

            // Start recording the remote audio stream
            startRecording(stream);
          } else if (attempt < 12) {
            setTimeout(() => tryAttachAudio(attempt + 1), 250);
          } else {
            console.warn('[Dialer] Could not attach remote audio after retries');
          }
        };
        tryAttachAudio(0);

        // Enable server-side noise suppression for cleaner audio to the remote party
        const callControlId = call.telnyxCallControlId;
        if (callControlId) {
          axios.post(`${API}/telnyx/noise-suppression`, {
            call_control_id: callControlId,
            direction: 'both',
          }).catch(e => console.error('Noise suppression not available:', e.message));
        }

        toast.success('Call connected');
        if (onCallConnectedRef.current) onCallConnectedRef.current(phoneNumber);
        break;

      case 'hangup':
      case 'destroy':
      case 'purge': {
        stopDialTone();
        setCallState('ended');
        stopDurationTimer();
        const finalDuration = callDurationRef.current;
        // SIP INVITE was rejected before the call ever became active → surface
        // as an error so the caller can hang up the parked leg immediately.
        if (!callReachedActiveRef.current) {
          if (onErrorRef.current) onErrorRef.current('SIP join failed — call rejected before connect');
          break;
        }
        // Stop recording, upload, then notify parent with recording_id
        stopAndUpload(finalDuration).then((recordingId) => {
          if (onCallEndedRef.current) onCallEndedRef.current(finalDuration, recordingId);
        });
        break;
      }
      default:
        break;
    }
  }, [audioSettings, phoneNumber, startDurationTimer, stopDurationTimer, startRecording, stopAndUpload]);

  // Keep the ref current so the notification handler always calls the latest version
  useEffect(() => {
    handleCallStateChangeRef.current = handleCallStateChange;
  }, [handleCallStateChange]);

  const handleNotification = useCallback((notification) => {
    const call = notification.call;
    if (!call) return;


    switch (notification.type) {
      case 'callUpdate':
        if (handleCallStateChangeRef.current) {
          handleCallStateChangeRef.current(call);
        }
        break;
      case 'userMediaError':
        console.error('Microphone not found or access denied');
        setError('Microphone not found. Using phone call instead.');
        setCallState('idle');
        if (onErrorRef.current) onErrorRef.current('Microphone not available');
        break;
      default:
        break;
    }
  }, []);

  const makeCall = useCallback(async (telnyxClient) => {
    if (!phoneNumber) return;

    // Guard: prevent duplicate calls (e.g. if telnyx.ready fires twice)
    if (hasCalledRef.current) {
      return;
    }
    hasCalledRef.current = true;

    try {
      setCallState('connecting');

      // Verify microphone access
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
      } catch (mediaError) {
        console.error('Microphone access error:', mediaError);
        const errorMsg = mediaError.name === 'NotFoundError'
          ? 'No microphone found. Using phone call instead.'
          : 'Microphone access denied. Please allow microphone access.';
        setError(errorMsg);
        setCallState('idle');
        if (onErrorRef.current) onErrorRef.current(errorMsg);
        return;
      }

      // Clean phone number. SIP URIs pass through unchanged — only bare PSTN
      // numbers get the +1 country-code prefix added.
      let cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
      const isSipUri = cleanNumber.startsWith('sip:') || cleanNumber.startsWith('sips:');
      if (!isSipUri && !cleanNumber.startsWith('+')) cleanNumber = '+1' + cleanNumber;

      // Get from number and caller name from backend
      const statusRes = await axios.get(`${API}/telnyx/status`);
      const fromNumber = statusRes.data.phone_number || '';
      const callerName = statusRes.data.caller_name || '';

      const audioConstraints = getVoiceAudioConstraints(audioSettings?.microphone_id);
      const preferred_codecs = getHDVoiceCodecs();

      const rnnoiseEnabled = audioSettings?.rnnoise_enabled !== false; // default on
      const filtered = await getNoiseFilteredStream(audioSettings?.microphone_id, rnnoiseEnabled);

      const callOptions = {
        destinationNumber: cleanNumber,
        callerNumber: fromNumber,
        callerName: callerName,
        video: false,
        useStereo: false,
        ...(preferred_codecs ? { preferred_codecs } : {}),
      };

      if (filtered) {
        callOptions.localStream = filtered.stream;
        rnnoiseCleanupRef.current = filtered.cleanup;
        setActiveNoiseFilter(filtered.filterType);
      } else {
        callOptions.audio = audioConstraints;
        setActiveNoiseFilter(null);
      }

      const call = telnyxClient.newCall(callOptions);
      setCurrentCall(call);

    } catch (err) {
      console.error('Failed to make call:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to initiate call';
      setError(errorMsg);
      setCallState('idle');
      if (onErrorRef.current) onErrorRef.current(errorMsg);
    }
  }, [audioSettings, phoneNumber]);

  // Initialize TelnyxRTC client when dialog opens
  // NOTE: onError intentionally NOT in deps — use onErrorRef instead to avoid re-init on parent re-render
  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    let telnyxClient = null;

    // Reset per-call state
    hasCalledRef.current = false;

    const handleUncaughtError = (event) => {
      if (event.message && (
        event.message.includes('getTransceivers') ||
        event.message.includes('TelnyxRTC') ||
        event.message.includes('RTCPeerConnection')
      )) {
        event.preventDefault();
        event.stopPropagation();
        console.error('Caught Telnyx WebRTC error:', event.message);
        if (isMounted) {
          setError('WebRTC not available. Switching to phone call...');
          setCallState('idle');
          if (onErrorRef.current) onErrorRef.current('WebRTC initialization failed');
        }
        return true;
      }
    };

    const handleUnhandledRejection = (event) => {
      if (event.reason && String(event.reason).includes('getTransceivers')) {
        event.preventDefault();
        console.error('Caught unhandled rejection:', event.reason);
        if (isMounted) {
          setError('WebRTC not available. Switching to phone call...');
          setCallState('idle');
          if (onErrorRef.current) onErrorRef.current('WebRTC initialization failed');
        }
        return true;
      }
    };

    window.addEventListener('error', handleUncaughtError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

    const initClient = async () => {
      try {
        if (!isMounted) return;
        setCallState('connecting');
        setError(null);

        if (!window.RTCPeerConnection) {
          throw new Error('WebRTC is not supported in this browser');
        }

        // Get WebRTC credentials with retry for rate limiting
        let credRes;
        let retries = 3;
        while (retries > 0) {
          try {
            credRes = await axios.get(`${API}/telnyx/webrtc-credentials`);
            break;
          } catch (err) {
            if (err.response?.status === 429 && retries > 1) {
              retries--;
              await new Promise(r => setTimeout(r, 2000));
              continue;
            }
            throw err;
          }
        }

        if (!isMounted) return;

        const { username, password } = credRes.data;
        if (!username || !password) throw new Error('WebRTC credentials not configured');

        // Verify RTCPeerConnection
        try {
          const testPc = new RTCPeerConnection();
          if (typeof testPc.getTransceivers !== 'function') {
            testPc.close();
            throw new Error('RTCPeerConnection.getTransceivers not supported');
          }
          testPc.close();
        } catch (rtcError) {
          throw new Error('WebRTC not fully supported. Using server-side calling instead.');
        }

        if (!isMounted) return;

        try {
          telnyxClient = new TelnyxRTC({
            login: username,
            password: password,
            ringtoneFile: null,
            ringbackFile: null,
            prefetchIceCandidates: true,
          });
        } catch (telnyxError) {
          console.error('Failed to create TelnyxRTC client:', telnyxError);
          throw new Error('Failed to initialize call client. Please check browser permissions.');
        }

        if (!isMounted) {
          try { telnyxClient.disconnect(); } catch(e) {}
          return;
        }

        telnyxClient.on('telnyx.ready', () => {
          if (isMounted) {
            setCallState('idle');
            makeCall(telnyxClient);
          }
        });

        telnyxClient.on('telnyx.error', (error) => {
          console.error('Telnyx error:', error);
          if (isMounted) {
            setError(error.message || 'Connection error');
            setCallState('idle');
            if (onErrorRef.current) onErrorRef.current(error.message || 'Connection error');
          }
        });

        telnyxClient.on('telnyx.socket.error', (error) => {
          console.error('Socket error:', error);
          if (isMounted) {
            setError('Connection failed. Please check your internet connection.');
            setCallState('idle');
            if (onErrorRef.current) onErrorRef.current('Connection failed');
          }
        });

        telnyxClient.on('telnyx.notification', (notification) => {
          if (isMounted) {
            handleNotification(notification);
          }
        });

        const connectPromise = telnyxClient.connect();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Connection timeout')), 10000)
        );

        try {
          await Promise.race([connectPromise, timeoutPromise]);
          if (isMounted) setClient(telnyxClient);
        } catch (connectError) {
          throw new Error('Failed to connect to call service. Please try again.');
        }

      } catch (err) {
        console.error('Failed to initialize Telnyx:', err);
        if (isMounted) {
          const errorMsg = err.response?.status === 429
            ? 'Too many requests. Please wait a moment and try again.'
            : err.response?.data?.detail || err.message || 'Failed to connect';
          setError(errorMsg);
          setCallState('idle');
          if (onErrorRef.current) onErrorRef.current(errorMsg);
        }
      }
    };

    const initTimeout = setTimeout(() => { initClient(); }, 0);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      window.removeEventListener('error', handleUncaughtError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
      if (telnyxClient) {
        try { telnyxClient.disconnect(); } catch (e) {}
      }
      if (client) {
        try { client.disconnect(); } catch (e) {}
      }
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
      // Stop dial tone on cleanup
      if (dialToneIntervalRef.current) clearInterval(dialToneIntervalRef.current);
      if (dialToneCtxRef.current) { try { dialToneCtxRef.current.close(); } catch(e) {} }
      // Clean up RNNoise filter
      if (rnnoiseCleanupRef.current) { rnnoiseCleanupRef.current(); rnnoiseCleanupRef.current = null; }
    };
  }, [isOpen]); // onError intentionally excluded — using onErrorRef

  const hangUp = () => {
    if (currentCall) {
      try { currentCall.hangup(); } catch (e) { console.error('Hangup error:', e); }
    }
    // Recording stop + upload is handled by the hangup state handler in handleCallStateChange
    // Just ensure recorder is stopped if state handler doesn't fire
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    // Clean up RNNoise filter
    if (rnnoiseCleanupRef.current) {
      rnnoiseCleanupRef.current();
      rnnoiseCleanupRef.current = null;
    }
    setCallState('ended');
    stopDurationTimer();
  };

  const toggleMute = () => {
    if (currentCall) {
      if (isMuted) {
        currentCall.unmuteAudio();
      } else {
        currentCall.muteAudio();
      }
      setIsMuted(!isMuted);
    }
  };

  const toggleDeafen = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = !isDeafened;
      setIsDeafened(!isDeafened);
    }
  };

  const toggleHold = () => {
    if (currentCall) {
      if (isOnHold) {
        currentCall.unhold();
      } else {
        currentCall.hold();
      }
      setIsOnHold(!isOnHold);
    }
  };

  const handleClose = () => {
    if (currentCall && callState === 'active') hangUp();
    if (client) {
      try { client.disconnect(); } catch (e) {}
    }
    // Clean up RNNoise if not already done by hangUp
    if (rnnoiseCleanupRef.current) {
      rnnoiseCleanupRef.current();
      rnnoiseCleanupRef.current = null;
    }
    setClient(null);
    setCurrentCall(null);
    setCallState('idle');
    setCallDuration(0);
    setError(null);
    onClose();
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusMessage = () => {
    switch (callState) {
      case 'connecting': return 'Connecting...';
      case 'ringing':    return 'Ringing...';
      case 'active':     return formatDuration(callDuration);
      case 'ended':      return `Call ended - ${formatDuration(callDuration)}`;
      default:           return 'Ready';
    }
  };

  if (!isOpen) return null;

  const statusBgColor = error ? 'from-red-600 to-red-500' :
    callState === 'active' ? 'from-emerald-600 to-teal-500' :
    callState === 'connecting' || callState === 'ringing' ? 'from-emerald-600 to-teal-500' :
    callState === 'ended' ? 'from-slate-600 to-slate-500' :
    'from-slate-500 to-slate-400';

  // Minimized: small floating pill bottom-right
  if (isMinimized) {
    return ReactDOM.createPortal(
      <>
        <audio ref={remoteAudioRef} autoPlay style={{ position: 'absolute', top: '-9999px', left: '-9999px' }} />
        <button
          onClick={() => setIsMinimized(false)}
          className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-2 text-white rounded-full pl-3 pr-4 py-3 shadow-lg transition-all duration-200 hover:scale-105 ${
            callState === 'active' ? 'bg-emerald-600 hover:bg-emerald-700' :
            error ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-600 hover:bg-emerald-700'
          }`}
          data-testid="webrtc-call-minimized"
        >
          <div className="relative">
            <Phone className="h-5 w-5" />
            {callState === 'active' && (
              <>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-300 rounded-full animate-ping" />
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-300 rounded-full" />
              </>
            )}
          </div>
          <div className="text-left">
            <p className="text-xs font-semibold leading-tight">{leadName || 'Call'}</p>
            <p className="text-xs font-mono opacity-80">{getStatusMessage()}</p>
          </div>
        </button>
      </>,
      document.body
    );
  }

  // Expanded: centered floating panel
  return ReactDOM.createPortal(
    <>
      <audio ref={remoteAudioRef} autoPlay style={{ position: 'absolute', top: '-9999px', left: '-9999px' }} />

      {/* Semi-transparent backdrop */}
      <div className="fixed inset-0 z-[9998] bg-black/40" />

      {/* Centered card */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-full max-w-sm"
        style={{ animation: 'dialerScaleIn 0.15s ease-out' }}
        data-testid="webrtc-call-widget"
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header with gradient */}
          <div className={`bg-gradient-to-r ${statusBgColor} px-5 py-4 flex items-center justify-between`}>
            <div className="flex items-center gap-3 text-white">
              <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
                {callState === 'connecting' ? (
                  <Loader2 className="h-5 w-5 text-white animate-spin" />
                ) : callState === 'ringing' ? (
                  <PhoneCall className="h-5 w-5 text-white animate-pulse" />
                ) : (
                  <Phone className="h-5 w-5 text-white" />
                )}
              </div>
              <div>
                <p className="font-bold text-sm">{leadName || 'Outgoing Call'}</p>
                <p className="text-2xl font-mono font-bold opacity-95">{getStatusMessage()}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1.5 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                title="Minimize"
              >
                <Minus className="h-4 w-4" />
              </button>
              {callState === 'ended' && (
                <button
                  onClick={handleClose}
                  className="p-1.5 rounded-lg hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="p-5">
            {/* Phone Number */}
            <p className="text-center text-lg font-mono font-bold text-slate-800">{phoneNumber}</p>

            {/* Company name if available */}
            {companyName && (
              <p className="text-center text-sm text-slate-500 mt-0.5">{companyName}</p>
            )}

            {/* Noise filter & recording indicators */}
            <div className="flex items-center justify-center gap-3 mt-3 mb-1">
              {isRecording && (
                <span className="inline-flex items-center gap-1.5 text-red-500 text-xs">
                  <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                  Recording
                </span>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-center my-4">
                <p className="text-red-500 text-sm bg-red-50 p-3 rounded-xl mb-2">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-lg"
                  onClick={() => {
                    setError(null);
                    setCallState('connecting');
                    if (client) {
                      try { client.disconnect(); } catch(e) {}
                    }
                    setClient(null);
                  }}
                  data-testid="retry-call-btn"
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-slate-100 my-4" />

            {/* Controls */}
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="flex flex-col items-center gap-1">
                <Button
                  onClick={toggleMute}
                  variant="outline"
                  size="icon"
                  className={`h-12 w-12 rounded-full border-2 transition-all ${isMuted ? 'bg-red-50 text-red-600 border-red-300 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                  disabled={callState !== 'active'}
                  title={isMuted ? 'Unmute' : 'Mute'}
                  data-testid="mute-btn"
                >
                  {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </Button>
                <span className="text-[10px] text-slate-400">{isMuted ? 'Unmute' : 'Mute'}</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Button
                  onClick={toggleDeafen}
                  variant="outline"
                  size="icon"
                  className={`h-12 w-12 rounded-full border-2 transition-all ${isDeafened ? 'bg-amber-50 text-amber-600 border-amber-300 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                  disabled={callState !== 'active'}
                  title={isDeafened ? 'Undeafen' : 'Deafen'}
                  data-testid="deafen-btn"
                >
                  {isDeafened ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </Button>
                <span className="text-[10px] text-slate-400">{isDeafened ? 'Unmute' : 'Speaker'}</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Button
                  onClick={toggleHold}
                  variant="outline"
                  size="icon"
                  className={`h-12 w-12 rounded-full border-2 transition-all ${isOnHold ? 'bg-yellow-50 text-yellow-700 border-yellow-300 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                  disabled={callState !== 'active'}
                  title={isOnHold ? 'Resume call' : 'Hold call'}
                  data-testid="hold-btn"
                >
                  {isOnHold ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                </Button>
                <span className="text-[10px] text-slate-400">{isOnHold ? 'Resume' : 'Hold'}</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Button
                  onClick={hangUp}
                  size="icon"
                  className="h-12 w-12 rounded-full bg-red-500 hover:bg-red-600 text-white border-2 border-red-500 hover:border-red-600"
                  disabled={callState === 'idle' || callState === 'ended'}
                  title="End Call"
                  data-testid="hangup-btn"
                >
                  <PhoneOff className="h-5 w-5" />
                </Button>
                <span className="text-[10px] text-red-400">End</span>
              </div>
            </div>

            <DTMFKeypad call={currentCall} wrapperClass="mt-1" />
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

export default function TelnyxWebRTCCall(props) {
  return (
    <WebRTCErrorBoundary
      isOpen={props.isOpen}
      onClose={props.onClose}
      onError={props.onError}
    >
      <TelnyxWebRTCCallInner {...props} />
    </WebRTCErrorBoundary>
  );
}
